import { Typography } from '@the-platform-group/component-library'
import { DataGrid, DataGridActionCell } from '@the-platform-group/component-library/DataGrid'
import {
  ACTIONS,
  DATE,
  NUMBER,
  PERCENTAGE,
} from '@the-platform-group/component-library/DataGrid/constant'
import { useProcessingQueueData, useProcessingQueueMutation } from './hooks/useProcessingQueue'
import { humanizeSnakeCase } from 'utils/format'
import {
  CheckCircle as ApproveIcon,
  RemoveCircle as RejectIcon,
} from '@the-platform-group/component-library/Icons'
import { useConfirmationDialog } from 'components/ConfirmationDialog'
import FileUploadModalOpenButton from './components/FileUploadModalOpenButton'

const TABLE_NAME = 'noboProcessingQueue'

export const getColumns = ({ handleApprove, handleReject }) => {
  return [
    {
      field: 'state',
      valueGetter: ({ value }) => humanizeSnakeCase(value),
    },
    {
      field: 'uploadedBy',
    },
    {
      field: 'originalFilename',
      headerName: 'Original Filename',
    },
    {
      field: 'cusip',
      headerName: 'CUSIP',
    },
    {
      field: 'datasource',
    },
    {
      field: 'reportDate',
      type: DATE,
      dateFormat: 'MMM DD, YYYY',
    },
    {
      field: 'failureRate',
      valueGetter: ({ value }) => value * 100,
      type: PERCENTAGE,
      precision: 3,
    },
    {
      field: 'parsingFailures',
      type: NUMBER,
    },
    {
      field: 'normalizedNoboCount',
      type: NUMBER,
      headerName: 'Normalized NOBO Count',
    },
    {
      field: 'createdAt',
      type: DATE,
      dateFormat: 'MMM DD, YYYY',
    },
    {
      field: 'actions',
      type: ACTIONS,
      getActions: ({ row }) => [
        ...(row.isApprovable
          ? [
              <DataGridActionCell
                icon={<ApproveIcon />}
                color="primary"
                title="Approve"
                onClick={() =>
                  handleApprove({
                    id: row.id,
                    cusip: row.cusip,
                    originalFilename: row.originalFilename,
                  })
                }
              />,
            ]
          : []),
        ...(row.isRejectable
          ? [
              <DataGridActionCell
                icon={<RejectIcon />}
                color="error"
                title="Reject"
                onClick={() =>
                  handleReject({
                    id: row.id,
                    cusip: row.cusip,
                    originalFilename: row.originalFilename,
                  })
                }
              />,
            ]
          : []),
      ],
    },
  ]
}

/**
 * Component for displaying the processing queue for NOBO reports.
 *
 * @returns {React.ReactElement} The rendered processing queue component
 */
const ProcessingQueue = () => {
  const { data, isLoading } = useProcessingQueueData()

  const {
    approveProcessingQueueMutation,
    rejectProcessingQueueMutation,
  } = useProcessingQueueMutation()
  const { createConfirmationDialog } = useConfirmationDialog()

  const handleApprove = ({ id, cusip, originalFilename }) => {
    createConfirmationDialog({
      title: 'Approve Report',
      body: `Are you sure you want to approve this report for ${cusip} - ${originalFilename}?`,
      confirmButtonText: 'Approve',
      onConfirm: () => approveProcessingQueueMutation.mutate(id),
    })
  }

  const handleReject = ({ id, cusip, originalFilename }) => {
    createConfirmationDialog({
      title: 'Reject Report',
      body: `Are you sure you want to reject this report for ${cusip} - ${originalFilename}?`,
      confirmButtonText: 'Reject',
      onConfirm: () => rejectProcessingQueueMutation.mutate(id),
    })
  }

  return (
    <>
      <Typography variant="h4" component="h1" gutterBottom>
        NOBO Processing Queue
      </Typography>
      <DataGrid
        autoHeight
        advancedToolbarProps={{
          tableName: TABLE_NAME,
          slots: {
            customContentList: [<FileUploadModalOpenButton />],
          },
        }}
        columns={getColumns({
          handleApprove,
          handleReject,
        })}
        rows={data}
        loading={isLoading}
        name={TABLE_NAME}
        pagination={false}
      />
    </>
  )
}

export default ProcessingQueue
